import clsx from "clsx";
import type React from "react";
import { forwardRef, useEffect, useState } from "react";
import { useChatProvider } from "../../hooks/useChatProvider";
import {
  clearPhone,
  formatPrice,
  getAddressByCep,
  maskString,
} from "../../utils/functions";
import { container, functionContainer, styledTable } from "./styles.css";

interface FunctionsProps extends React.HTMLAttributes<HTMLDivElement> {}

const FORMAT_PRICE_VALUES = [
  89.9,
  "89.90",
  "89,90",
  89.9999,
  "1.000,50",
  "405",
];

const CEP = "29050705";

export const Functions = forwardRef(function Functions(
  { className, ...props }: FunctionsProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const [viacep, setViacep] = useState<string | null>(null);

  useChatProvider();

  useEffect(() => {
    const getCeps = async () => {
      const resViacep = await getAddressByCep(CEP);

      setViacep(JSON.stringify(resViacep, null, 2));
    };

    getCeps();
  }, []);

  return (
    <div ref={ref} className={clsx(container, className)} {...props}>
      <div className={functionContainer}>
        <h2>formatPrice(price, options)</h2>
        <p>
          Função que formata um valor numérico ou string para o formato de preço
          brasileiro.
        </p>
        <p>
          <b>price: </b> string | number;
        </p>
        <p>
          <b>options?:</b>
          {"{hasPrefix?: boolean , minimumFractionDigits?: number}"}; default:{" "}
          {"{hasPrefix: true, minimumFractionDigits: 2}"}
        </p>
        <table className={styledTable}>
          <thead>
            <tr>
              <th colSpan={2}>formatPrice</th>
            </tr>
            <tr>
              <th>Entrada</th>
              <th>Saída</th>
            </tr>
          </thead>
          <tbody>
            {FORMAT_PRICE_VALUES.map((value, index) => (
              <tr key={index}>
                <td>{typeof value === "string" ? `"${value}"` : value}</td>
                <td>{formatPrice(value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={functionContainer}>
        <h2>maskString(string, percent, char)</h2>
        <p>Função que mascara parte de uma string</p>
        <p>
          <b>string: </b> string;
        </p>
        <p>
          <b>percent: </b> number; default: 50
        </p>
        <p>
          <b>char: </b> string; default: "*"
        </p>
        <table className={styledTable}>
          <thead>
            <tr>
              <th colSpan={2}>maskString</th>
            </tr>
            <tr>
              <th>Entrada</th>
              <th>Saída</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{"String de exemplo"}</td>
              <td>{maskString("String de exemplo")}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className={functionContainer}>
        <h2>getAddressByCep(cep)</h2>
        <p>Função que retorna um endereço baseado no CEP fornecido</p>
        <p>
          <b>cep: </b> string;
          <b>provider?: 'viacep' | 'brasilapi'. Default: 'viacep'</b>
        </p>
        <table className={styledTable}>
          <thead>
            <tr>
              <th colSpan={3}>getAddressByCep</th>
            </tr>
            <tr>
              <th>Provedor</th>
              <th>Entrada</th>
              <th>Saída</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Via cep</td>
              <td>{CEP}</td>
              <td style={{ whiteSpace: "pre", textAlign: "left" }}>{viacep}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className={functionContainer}>
        <h2>clearPhone(phone)</h2>
        <p>
          Função que limpa uma string de telefone e retorna apenas os números
        </p>
        <p>
          <b>phone: </b> string;
        </p>
        <table className={styledTable}>
          <thead>
            <tr>
              <th colSpan={2}>maskString</th>
            </tr>
            <tr>
              <th>Entrada</th>
              <th>Saída</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{"(28) 99961-8010"}</td>
              <td>{clearPhone("(28) 99961-8010")}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
});

Functions.displayName = "Functions";
